import { mapActions, mapGetters } from 'vuex';
import { CHATBOT_COMMANDS } from '~/utils/chat-flags';
import { getInvoices } from '~/api/invoices';
import { getPaymentsBillings } from '~/api/payments';
import { getPhoneCreditPrepaidTransaction, patchPhoneCreditPrepaidTransactionStatus } from '~/api/phone-credit-prepaid';
import { retrieveDanaProfile } from '~/api/dana-wallet';
import { getMe } from '~/api/agent';

export default {
  computed: {
    ...mapGetters({
      isLoggedIn: 'auth/isUserLoggedIn',
      changeNumberImageData: 'change-phone-number/getRequiredImage',
    }),
  },
  methods: {
    ...mapActions({
      fetchNeoConfigs: 'neo/fetchNeoConfigs',
      sendMessage: 'zopim/message/sendMessage',
    }),

    /**
     * Check current transaction's refund eligibility
     * @command - [apicall] checkAutoRefundEligibility [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     * @Note - This function currently applies ONLY to phone-credit-prepaid
     */
    async checkAutoRefundEligibility(currentTrx = null) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        autoRefundIsEligible: '[apiresponse] autoRefundIsEligible [/apiresponse]',
        autoRefundIsNotEligible: '[apiresponse] autoRefundIsNotEligible [/apiresponse]',
        autoRefundEligibiltyIsError: '[apiresponse] autoRefundEligibiltyIsError [/apiresponse]',
      };
      const eligibleTypes = ['phone-credit-prepaid'];

      try {
        const CONFIG_KEY = 'csm-auto-refund-eligibility';
        const {
          data: [config],
        } = await this.fetchNeoConfigs([CONFIG_KEY]);
        /**
         * response
         * {
            "type": <string>,
            "nominal": <number>,
            "state": <string>,
            "max_refund_time_minute": <number>,
            "excluded_payment_method": <string[]>
          }
        */
        const {
          type,
          max_nominal: maxNominal,
          state,
          max_refund_time_minute: maxRefundTimeMinute,
          excluded_payment_method: excludedPaymentMethod,
        } = config.data || {};

        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        // Simply send not eligible response if the transaction type is not onthe eligibility list
        if (!eligibleTypes.includes(invoiceTrx.type)) {
          this.$zopim.sendChatMsg(responses.autoRefundIsNotEligible);
          return;
        }

        // Get phone-credit-prepaid transaction data
        const { data: phoneCreditPrepaidData } = await getPhoneCreditPrepaidTransaction(this.$axios, invoiceTrx?.id);

        // Get paid at time with current time difference
        const nowTime = new Date().getTime();
        const paidAtTime = new Date(phoneCreditPrepaidData.state_changed_at.paid_at).getTime();
        const paidTimeDifference = Math.floor((nowTime - paidAtTime) / 1000); // difference between time now and the time of paid_at
        const maxRefundTimeInSeconds = maxRefundTimeMinute * 60; // convert the max refund time limit to seconds

        const eligibilityValidation = {
          type: phoneCreditPrepaidData.type === type,
          maxNominal: phoneCreditPrepaidData.nominal <= maxNominal,
          state: phoneCreditPrepaidData.state === state,
          maxRefundTimeMinute: paidTimeDifference >= maxRefundTimeInSeconds,
          excludedPaymentMethod: !excludedPaymentMethod.includes(invoiceData.payment_type),
        };

        const isEligible = Object.values(eligibilityValidation).reduce((prev, curr) => prev && curr, true);

        /**
         * Send responses
         */
        if (isEligible) {
          this.$zopim.sendChatMsg(responses.autoRefundIsEligible);
          return;
        }
        this.$zopim.sendChatMsg(responses.autoRefundIsNotEligible);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.autoRefundEligibiltyIsError);
        console.error('[ERROR] Something is wrong when trying to check auto refund eligibilty', error);
      }
    },

    /**
     * Get the media from neo config
     * @config - csm-mitra-chatbot-media
     * @param {string} command - full msg with the format of '[<flag>] <message> [/<flag>]'
     */
    async showMediaFromChatbot(command = '') {
      const responses = {
        error: '[response] mediaError [/response]',
        notFound: '[response] mediaNotFound [/response]',
      };

      try {
        const CONFIG_KEY = 'csm-mitra-chatbot-media';
        const {
          data: [config],
        } = await this.fetchNeoConfigs([CONFIG_KEY]);
        /**
         * response should be like
         * {
            "data": [
              {
                "url": "https://www.w3schools.com/html/mov_bbb.mp4",
                "type": "image" | "video",
                "command": "[image] imageExample [/image]" | "[video] videoExample [/video]"
              },
              ...
            ],
          }
        */
        const { media: mediaData } = config.data || {};
        const selectedMedia = mediaData?.find(media => media.command === command);

        if (!selectedMedia) {
          this.$zopim.sendChatMsg(responses.notFound);
          return;
        }

        const msgPayload = {
          msg: selectedMedia.url,
          nick: 'agent:trigger', // impersonate Acha Bot
          displayName: 'Acha',
          type: selectedMedia.type,
        };
        this.sendMessage({ ...msgPayload });
      } catch (error) {
        this.$zopim.sendChatMsg(responses.error);
        console.error('[ERROR] Something is wrong when trying to show media from chatbot', error);
      }
    },

    /**
     * Check status of kirim uang/digital-send transactions
     * @command - [apicall] executeRefundMitra [/apicall]
     * @param {object} currentTrx - currently selected transaction object
     */
    async executeRefundMitra(currentTrx = null) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const responses = {
        executeRefundMitraSuccess: '[apiresponse] executeRefundMitraSuccess [/apiresponse]',
        executeRefundMitraFailed: '[apiresponse] executeRefundMitraFailed [/apiresponse]',
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const {
          transactions: [invoiceTrx],
        } = invoiceData;

        const { meta } = await patchPhoneCreditPrepaidTransactionStatus(this.$axios, {
          transactionId: invoiceTrx.id,
          action: 'cancel_process',
        });

        if (meta.http_status === 200) {
          this.$zopim.sendChatMsg(responses.executeRefundMitraSuccess);
          return;
        }
        this.$zopim.sendChatMsg(responses.executeRefundMitraFailed);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.executeRefundMitraFailed);
        console.error('[ERROR] Something is wrong when trying to execute refund mitra', error);
      }
    },

    /**
     * Check Saldo wallet
     * @command - [apicall] checkSaldoWallet [/apicall]
     */
    async checkSaldoWallet() {
      const responses = {
        checkSaldoWalletElligible: '[apiresponse] saldoWalletElligible [/apiresponse]',
        checkSaldoWalletNotElligible: '[apiresponse] saldoWalletNotElligible [/apiresponse]',
        checkSaldoWalletFailed: '[apiresponse] saldoWalletIsError [/apiresponse]',
      };

      try {
        // Get dana profile
        const { data: danaProfile } = await retrieveDanaProfile(this.$axios)
          .then(res => res)
          .catch(() => {
            return { data: null };
          });
        // Get agents me
        const { data: agentsMe } = await getMe(this.$axios);

        const danaBalance = danaProfile?.balance || 0;
        const saldoCredits = agentsMe?.dompet.saldo_credits || 0;
        const saldoDompet = agentsMe.dompet?.saldo_dompet || 0;
        const balance = saldoCredits + saldoDompet + danaBalance;

        if (balance < 500000) {
          this.$zopim.sendChatMsg(responses.checkSaldoWalletElligible);
        } else {
          this.$zopim.sendChatMsg(responses.checkSaldoWalletNotElligible);
        }
      } catch (error) {
        this.$zopim.sendChatMsg(responses.checkSaldoWalletFailed);
        console.error('[ERROR] Something is wrong when trying to check saldo wallet', error);
      }
    },

    /**
     * Checks invoice status of any type of transactions
     * Currently being used by Top Up saldo mitra transaction
     * @command - '[apicall] checkInvoiceStatus [/apicall]'
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkInvoiceStatus(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      // Possible statuses
      // pending, paid, expired and cancelled.
      const responses = {
        invoiceStatus: '[apiresponse] invoiceStatus<status> [/apiresponse]',
        invoiceStatusError: '[apiresponse] invoiceStatusError [/apiresponse]',
      };

      try {
        // Get invoice data
        const {
          data: [invoiceData],
        } = await getInvoices(this.$axios, { keywords: currentTrx.paymentId });
        const { state } = invoiceData;
        const capitalizedState = state.charAt(0).toUpperCase() + state.slice(1);
        const finalResponse = responses.invoiceStatus.replace('<status>', capitalizedState);
        this.$zopim.sendChatMsg(finalResponse);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.invoiceStatusError);
        console.error('[ERROR] Something is wrong when trying to check invoice status of the transaction', error);
      }
    },
    /**
     * Checks the currently logged in user's GMV,
     * from the transactions within 7 days ago
     * @command - '[apicall] checkUser7DaysGMV [/apicall]'
     */
    async checkUser7DaysGMV() {
      const responses = {
        user7DaysGMVNotIdentified: '[apiresponse] user7DaysGMVNotIdentified [/apiresponse]',
        user7DaysGMVEligible: '[apiresponse] user7DaysGMVEligible [/apiresponse]',
        user7DaysGMVNotEligible: '[apiresponse] user7DaysGMVNotEligible [/apiresponse]',
        user7DaysGMVError: '[apiresponse] user7DaysGMVError [/apiresponse]',
      };

      // User is not logged in
      if (!this.isLoggedIn) {
        this.$zopim.sendChatMsg(responses.user7DaysGMVNotIdentified);
        return;
      }

      try {
        // Get invoices data #1
        const { data: invoicesData } = await getInvoices(this.$axios, {
          states: ['paid'],
          offset: 0,
          limit: 100,
        });
        const invoicesRow1Length = invoicesData.length;

        const { data: invoicesData2 } =
          invoicesRow1Length === 100
            ? await getInvoices(this.$axios, {
              states: ['paid'],
              offset: 100,
              limit: 100,
            })
            : { data: [] };

        const finalInvoicesData = [...invoicesData, ...invoicesData2];
        const sumAmount = finalInvoicesData.reduce((sum, inv) => sum + inv.amount.total, 0);

        /**
         * Eligible if sumAmount is less than Rp500.000 (< Rp500.000)
         */
        if (sumAmount < 500000) {
          this.$zopim.sendChatMsg(responses.user7DaysGMVEligible);
          return;
        }

        /**
         * Not Eligible if sumAmount is more than or equal to Rp500.000 (>= Rp500.000)
         */
        this.$zopim.sendChatMsg(responses.user7DaysGMVNotEligible);
        return;
      } catch (error) {
        this.$zopim.sendChatMsg(responses.user7DaysGMVError);
        console.error('[ERROR] Something is wrong when trying to check GMV for the last 7 days', error);
      }
    },

    /**
     * Checks payment mutation status of any type of transactions
     * Currently being used by Top Up saldo mitra transaction
     * @command - '[apicall] checkAutoRefundEligibility [/apicall]'
     * @param {object} currentTrx - currently selected transaction object
     */
    async checkPaymentBillings(currentTrx) {
      if (!currentTrx) {
        this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.noSelectedTransaction);
        return;
      }

      const { payment_id: paymentId } = currentTrx;

      const responses = {
        paymentBillingsFound: '[apiresponse] paymentBillingsFound [/apiresponse]',
        paymentBillingsNotFound: '[apiresponse] paymentBillingsNotFound [/apiresponse]',
        paymentBillingsError: '[apiresponse] paymentBillingsError [/apiresponse]',
      };

      try {
        // Get payment data
        const { data } = await getPaymentsBillings(this.$axios, paymentId);
        const isBillingsPaid = data.every(payment => payment.state === 'paid');

        if (isBillingsPaid) {
          this.$zopim.sendChatMsg(responses.paymentBillingsFound);
          return;
        }
        this.$zopim.sendChatMsg(responses.paymentBillingsNotFound);
      } catch (error) {
        this.$zopim.sendChatMsg(responses.paymentBillingsError);
        console.error('[ERROR] Something is wrong when trying to check invoice status of the transaction', error);
      }
    },
  },
};
